import React from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import CtaSection from '../components/CtaSection';
import { getOptimizedImageUrl } from '../lib/cms';

export default function AboutPage() {
  const values = [
    {
      title: 'Tæt sparring & nærvær',
      desc: 'Vi er jeres faste rådgiver på SoMe: vi sparrer om idéer, trends og formater, så jeres budskab rammer plet.',
    },
    {
      title: 'Professionel redigering & synlighed',
      desc: 'I fanger hverdagen – vi klipper råfilerne til skarpt, fængende content og sørger for en struktureret postkalender, der sikrer synlighed.',
    },
    {
      title: '0 binding & individuel tilpasning',
      desc: 'Ingen stive prispakker eller uoverskuelige flerårskontrakter. Samarbejdet tilpasses altid jeres forretnings aktuelle behov.',
    },
  ];

  return (
    <>
      <SeoHead
        title="Om os | SoMe Sparring, Redigering & Synlighed"
        description="Læs om Myserviceforyou: En engageret samarbejdspartner, der giver sparring på foto/video, klipper jeres content og opbygger synlighed med 0 binding."
      />

      {/* Hero */}
      <section className="pt-40 pb-20 md:pt-48 lg:pt-52 md:pb-28 bg-[#FAF8F5] border-b border-[#E8E2D9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-6">
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#EAE4DC] text-xs font-semibold uppercase tracking-widest text-[#4A4640]">
              Om Myserviceforyou
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#161514] leading-[1.1]">
              En fleksibel partner til{' '}
              <span className="text-[#876F50]">dine sociale medier</span>
            </h1>

            <p className="text-lg sm:text-xl text-[#5C5852] leading-relaxed">
              Vi hjælper hoteller, Airbnb-udlejere, restauranter, butikker og virksomheder med at opbygge en levende og professionel tilstedeværelse på præcis de platforme, I ønsker – uden tidsspild og uden dyre faste pakker.
            </p>
          </div>
        </div>
      </section>

      {/* Vores tilgang */}
      <section className="py-20 md:py-28 bg-[#FAFAF8] border-b border-[#E8E2D9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-6 rounded-3xl overflow-hidden shadow-md aspect-4/3 bg-[#161514] border border-[#E5DFD6]">
              <img
                src={getOptimizedImageUrl(
                  'https://assets.auxodev.dk/assets/d61c94bc-c5ec-439d-a220-afcc00eef7a0/agency-founders-meeting.png',
                  { width: 1000, quality: 85, format: 'auto' }
                )}
                alt="Myserviceforyou online SoMe rådgivning"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="lg:col-span-6 space-y-6">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#161514]">
                Hvorfor skal sociale medier være så besværligt?
              </h2>
              <div className="space-y-4 text-[#5C5852] text-base leading-relaxed">
                <p>
                  Mange virksomhedsejere, hotelværter og Airbnb-udlejere oplever, at sociale medier sluger dyrebar tid. Man ved godt, man bør lægge noget op, men hvad skal man filme? Hvem skal klippe det? Hvordan får man tekster på? Og hvornår har man tid til at planlægge kalenderen?
                </p>
                <p>
                  Hos Myserviceforyou har vi skabt en smidig model med fokus på sparring, redigering og synlighed: Vi sparrer med jer og vejleder jer i, hvordan I selv tager gode billeder og optager video med jeres smartphone – hvad enten det er stemningen i et boutique-hotel, kaffe og natur ved et unikt Airbnb eller hverdagen i en forretning. I sender optagelserne til os – og vi forvandler råklippene til fængende, professionelt redigerede videoer og styrer jeres postkalender.
                </p>
                <p>
                  Som udgangspunkt fanger I selv hverdagen, hvilket sparer jer for store produktionsomkostninger – og ved nærmere aftale er vi også fleksible til at komme ud til jer. I får et nærværende, autentisk udtryk på tværs af de platforme, der giver bedst mening for jeres forretning.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Værdier */}
      <section className="py-20 md:py-28 bg-[#F4EFEA] border-b border-[#E8E2D9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16 space-y-4">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#161514]">
              Det vi står for
            </h2>
            <p className="text-base text-[#5C5852]">
              Transparens, fleksibilitet og ægte værdi for din forretning.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((v, i) => (
              <div
                key={i}
                className="bg-[#FAF8F5] border border-[#E5DFD6] rounded-3xl p-8 space-y-4 shadow-xs"
              >
                <span className="text-3xl font-extrabold text-[#C5A880]">
                  0{i + 1}
                </span>
                <h3 className="text-xl font-bold text-[#161514]">
                  {v.title}
                </h3>
                <p className="text-sm text-[#5C5852] leading-relaxed">
                  {v.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaSection
        title="Lad os tage en uforpligtende snak"
        subtitle="Hver forretning er forskellig, og derfor har vi ingen faste prispakker. Kontakt os i dag, og udnyt vores kampagne: Betal for 2 måneder og få 1 måned gratis!"
        primaryButtonText="Kontakt os nu"
        primaryButtonLink="/kontakt"
        backgroundImage="https://assets.auxodev.dk/assets/d61c94bc-c5ec-439d-a220-afcc00eef7a0/production-editing-studio.png"
      />
    </>
  );
}
